import React, { useEffect, useState } from 'react';
import { db, collection, onSnapshot, query, orderBy, handleFirestoreError, OperationType, updateDoc, doc, deleteDoc, setDoc, auth } from '../firebase';
import { handleAsyncAction } from '../utils/AsyncActionHandler';
import { UserProfile, UserRole } from '../types';
import { useAuth } from '../App';
import { USER_ROLES, ROLE_LABELS } from '../constants';
import { 
  Shield, 
  UserCog, 
  Trash2, 
  Search, 
  Filter,
  MoreVertical,
  CheckCircle2,
  AlertCircle,
  Plus,
  X,
  Mail,
  Lock,
  RefreshCw,
  Key,
  Edit2,
  Loader2
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { sendPasswordResetEmail, Timestamp } from '../firebase';
import { initializeApp, getApps, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword as secondaryCreateUser } from 'firebase/auth';
import firebaseConfig from '../../firebase-applet-config.json';
import { toast } from 'sonner';
import ConfirmationModal from '../components/ConfirmationModal';
import { cn } from '../lib/utils';

const isProtectedAdmin = (email?: string) => {
  if (!email) return false;
  return email === 'ramitsolution.business@gmail.com' || email === 'jcksnmandela@gmail.com';
};

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<UserRole | 'all'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    role: USER_ROLES.TECHNICIAN as UserRole,
    password: '',
    department: '',
    isActive: true
  });
  const [editFormData, setEditFormData] = useState({
    displayName: '',
    role: USER_ROLES.TECHNICIAN as UserRole,
    department: ''
  });
  const { profile: currentUserProfile, isAdmin } = useAuth();

  if (!isAdmin) {
    return <div className="p-10 text-center font-bold text-slate-500">Access Denied: Admin only</div>;
  }

  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as UserProfile));
      setUsers(usersData);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'users');
    });
    return unsubscribe;
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    let secondaryApp: any = null;
    await handleAsyncAction(async () => {
      const existingApp = getApps().find(app => app.name === 'SecondaryApp');
      if (existingApp) {
        await deleteApp(existingApp);
      }
      secondaryApp = initializeApp(firebaseConfig, 'SecondaryApp');
      const secondaryAuth = getAuth(secondaryApp);

      // 1. Create Auth User
      const normalizedEmail = formData.email.toLowerCase();
      const userCredential = await secondaryCreateUser(secondaryAuth, normalizedEmail, formData.password);
      const user = userCredential.user;

      // 2. Create Firestore Profile
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: normalizedEmail,
        displayName: formData.displayName,
        role: formData.role,
        department: formData.department,
        isActive: true,
        createdAt: Timestamp.now()
      });

      // 3. Clean up secondary auth session
      try {
        await secondaryAuth.signOut();
      } catch (e) {}

      setIsModalOpen(false);
      setFormData({ 
        email: '', 
        displayName: '', 
        role: USER_ROLES.TECHNICIAN, 
        password: '', 
        department: '',
        isActive: true 
      });
    }, setIsSubmitting, {
      successMessage: 'User created successfully!',
      errorMessage: 'Error creating user',
      onError: async () => {
        if (secondaryApp) {
          try {
            await deleteApp(secondaryApp);
          } catch (e) {}
        }
      },
      onSuccess: async () => {
        if (secondaryApp) {
          try {
            await deleteApp(secondaryApp);
          } catch (e) {}
        }
      }
    });
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    await handleAsyncAction(async () => {
      await updateDoc(doc(db, 'users', selectedUser.uid), {
        displayName: editFormData.displayName,
        role: editFormData.role,
        department: editFormData.department
      });
      setIsEditModalOpen(false);
    }, setIsSubmitting, {
      successMessage: 'User updated successfully!',
      errorMessage: 'Error updating user'
    });
  };

  const handleResetPassword = async (email: string) => {
    setIsSubmitting(true);
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent to ' + email);
      setIsResetModalOpen(false);
    } catch (error: any) {
      toast.error('Error sending reset email: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()";
    let pass = "";
    for (let i = 0; i < 12; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password: pass });
  };
  const handleToggleStatus = async (userId: string, currentStatus: boolean | undefined) => {
    const newStatus = currentStatus === false ? true : false;
    try {
      await updateDoc(doc(db, 'users', userId), { isActive: newStatus });
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${userId}`);
    }
  };

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), { role: newRole });
      toast.success('User role updated');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${userId}`);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'users', userId));
          toast.success('User deleted successfully');
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `users/${userId}`);
        }
      }
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesFilter;
  });

  if (loading) return <div className="flex items-center justify-center h-64">Loading users...</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">User Management</h1>
          <p className="text-slate-500">Manage team roles and access permissions</p>
        </div>
        <button 
          onClick={() => { setIsSubmitting(false); setIsModalOpen(true); }}
          className="flex items-center justify-center gap-2 bg-blue-900 text-white px-6 py-3 rounded-2xl font-bold hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20"
        >
          <Plus className="w-5 h-5" />
          Add New User
        </button>
      </div>

      <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
            />
          </div>
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-2xl px-4 py-2 focus-within:ring-2 focus-within:ring-blue-800/10 transition-all">
            <Filter className="text-blue-400 w-4 h-4" />
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as any)}
              className="bg-transparent focus:outline-none text-sm font-bold text-blue-900 uppercase tracking-widest"
            >
              <option value="all">All Roles</option>
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="pb-4 font-black text-blue-900 uppercase text-[10px] tracking-widest">User</th>
                <th className="pb-4 font-black text-blue-900 uppercase text-[10px] tracking-widest">Role</th>
                <th className="pb-4 font-black text-blue-900 uppercase text-[10px] tracking-widest">Status</th>
                <th className="pb-4 font-black text-blue-900 uppercase text-[10px] tracking-widest">Joined</th>
                <th className="pb-4 font-black text-blue-900 uppercase text-[10px] tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredUsers.map((user) => (
                <tr key={user.uid} className="group hover:bg-blue-50/30 transition-colors">
                  <td className="py-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center border border-blue-100 overflow-hidden shadow-sm">
                        {user.photoURL ? (
                          <img src={user.photoURL} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <img src={`https://ui-avatars.com/api/?name=${user.displayName || user.email}&background=random`} alt="" />
                        )}
                      </div>
                      <div>
                        <p className="font-black text-blue-900 tracking-tight">{user.displayName || 'Unnamed User'}</p>
                        <p className="text-xs text-slate-400 font-medium">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-5">
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.uid, e.target.value as UserRole)}
                      disabled={user.uid === currentUserProfile?.uid}
                      className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-1.5 text-[10px] font-black text-blue-800 uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-800/10 disabled:opacity-50 transition-all cursor-pointer"
                    >
                      {Object.entries(ROLE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-5">
                    <span className={cn(
                      "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5",
                      user.isActive === false ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"
                    )}>
                      {user.isActive === false ? (
                        <>
                          <AlertCircle className="w-3 h-3" />
                          Deactivated
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-3 h-3" />
                          Active
                        </>
                      )}
                    </span>
                  </td>
                  <td className="py-5 text-xs text-slate-500 font-bold uppercase tracking-wider">
                    {user.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                  </td>
                  <td className="py-5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleToggleStatus(user.uid, user.isActive)}
                        disabled={user.uid === currentUserProfile?.uid}
                        className={cn(
                          "p-2.5 rounded-xl transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed",
                          user.isActive === false 
                            ? "text-emerald-500 hover:bg-emerald-50" 
                            : "text-red-400 hover:bg-red-50"
                        )}
                        title={user.isActive === false ? "Activate User" : "Deactivate User"}
                      >
                        {user.isActive === false ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => {
                          setIsSubmitting(false);
                          setSelectedUser(user);
                          setEditFormData({
                            displayName: user.displayName || '',
                            role: user.role,
                            department: user.department || ''
                          });
                          setIsEditModalOpen(true);
                        }}
                        className="p-2.5 text-slate-400 hover:text-blue-800 hover:bg-blue-50 rounded-xl transition-all cursor-pointer"
                        title="Edit User Role & Details"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setIsSubmitting(false);
                          setSelectedUser(user);
                          setIsResetModalOpen(true);
                        }}
                        className="p-2.5 text-slate-400 hover:text-orange-500 hover:bg-orange-50 rounded-xl transition-all cursor-pointer"
                        title="Reset Password"
                      >
                        <Key className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.uid)}
                        disabled={user.uid === currentUserProfile?.uid || isProtectedAdmin(user.email)}
                        className="p-2.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all disabled:opacity-0 cursor-pointer disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-blue-900/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-white rounded-[40px] shadow-2xl p-10"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-black text-blue-900 tracking-tight">Add New User</h2>
                <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleAddUser} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Full Name</label>
                  <input
                    required
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    placeholder="John Doe"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Email Address</label>
                  <input
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    placeholder="john@example.com"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Initial Password</label>
                    <div className="relative">
                      <input
                        required
                        type="text"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold pr-12"
                      />
                      <button 
                        type="button"
                        onClick={generatePassword}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Generate Random Password"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Role</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    >
                      {Object.entries(ROLE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Department</label>
                  <select
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  >
                    <option value="">Select Department</option>
                    {['Technical', 'Finance', 'Operations', 'Sales', 'IT', 'HR', 'Logistics', 'Billing', 'Management', 'Customer Care'].map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 px-6 py-4 border-2 border-slate-100 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-blue-800 transition-all shadow-xl shadow-blue-900/20 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create User'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit User Modal */}
      <AnimatePresence>
        {isEditModalOpen && selectedUser && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsEditModalOpen(false)}
              className="absolute inset-0 bg-blue-900/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-white rounded-[40px] shadow-2xl p-10"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-black text-blue-900 tracking-tight">Edit User</h2>
                <button onClick={() => setIsEditModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleEditUser} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest italic">Email (Read Only)</label>
                  <input
                    type="text"
                    value={selectedUser.email}
                    disabled
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-slate-400 font-bold opacity-70 cursor-not-allowed"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Full Name</label>
                  <input
                    required
                    type="text"
                    value={editFormData.displayName}
                    onChange={(e) => setEditFormData({ ...editFormData, displayName: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Role</label>
                    <select
                      value={editFormData.role}
                      onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value as UserRole })}
                      disabled={selectedUser.uid === currentUserProfile?.uid}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold disabled:opacity-50"
                    >
                      {Object.entries(ROLE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Department</label>
                    <select
                      value={editFormData.department}
                      onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    >
                      <option value="">Select Department</option>
                      {['Technical', 'Finance', 'Operations', 'Sales', 'IT', 'HR', 'Logistics', 'Billing', 'Management', 'Customer Care'].map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="flex-1 px-6 py-4 border-2 border-slate-100 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-blue-800 transition-all shadow-xl shadow-blue-900/20 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Changes'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isResetModalOpen && selectedUser && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsResetModalOpen(false)}
              className="absolute inset-0 bg-blue-900/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[40px] shadow-2xl p-10 text-center"
            >
              <div className="w-20 h-20 bg-orange-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <Key className="w-10 h-10 text-orange-500" />
              </div>
              <h2 className="text-2xl font-black text-blue-900 tracking-tight mb-2">Reset Password</h2>
              <p className="text-slate-500 mb-8">
                Send a password reset link to <span className="font-bold text-blue-900">{selectedUser.email}</span>?
              </p>

              <div className="flex gap-4">
                <button
                  onClick={() => setIsResetModalOpen(false)}
                  className="flex-1 px-6 py-4 border-2 border-slate-100 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleResetPassword(selectedUser.email)}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-4 bg-orange-500 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-orange-600 transition-all shadow-xl shadow-orange-500/20 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Link'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="YES"
        cancelLabel="NO"
        isDanger={true}
      />
    </div>
  );
};

export default UsersPage;
